import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { broker } from "@/config/broker.config";
import { FiArrowRight, FiCheck, FiPhone } from "@/lib/icons";
import { lenders, lenderStats } from "./_data/lenders.data";
import { LenderCard } from "./_components/LenderCard";

export function LendersPage() {
  return (
    <>
      {/* Hero Section */}
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text */}
            <div>
              <p className="section-label mb-4">Our Lender Panel</p>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight mb-6">
                Access to 70+
                <br />
                <span className="italic">trusted lenders</span>
              </h1>
              <p className="text-lg text-primary-foreground/90 max-w-lg mb-8">
                We compare loans from Australia's biggest banks to specialist
                lenders, finding you the right fit for your unique situation.
                One application, dozens of options.
              </p>

              <div className="flex flex-wrap gap-4">
                <Button
                  asChild
                  size="lg"
                  className="btn-pill bg-accent text-accent-foreground hover:bg-accent/90"
                >
                  <Link to="/contact">
                    Get Started
                    <FiArrowRight className="ml-2 size-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  className="btn-pill bg-white/10 border border-white/20 text-white hover:bg-white/20"
                >
                  <a href={`tel:${broker.phone.replace(/\s/g, "")}`}>
                    <FiPhone className="mr-2 size-4" />
                    {broker.phone}
                  </a>
                </Button>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: lenderStats.totalLenders, label: "Lenders on panel" },
                { value: lenderStats.majorBanks, label: "Major banks" },
                { value: lenderStats.approvalTime, label: "Typical approval" },
                { value: lenderStats.noFees, label: "Our service" },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="bg-primary-foreground/10 rounded-2xl p-6 text-center"
                >
                  <p className="font-serif text-3xl md:text-4xl text-primary-foreground mb-1">
                    {stat.value}
                  </p>
                  <p className="text-sm text-primary-foreground/80">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Use a Broker Section */}
      <section className="py-16 md:py-24 bg-cream">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="section-label mb-4">Why Use a Broker?</p>
              <h2 className="font-serif text-3xl md:text-4xl mb-6">
                One application,
                <br />
                <span className="italic">unlimited options</span>
              </h2>
              <p className="text-muted-foreground mb-6">
                When you go direct to a bank, you only see their products. As a
                broker, we compare your situation across 70+ lenders to find the
                loan that truly fits your needs.
              </p>

              <ul className="space-y-4">
                {[
                  "Compare rates from all major banks and specialist lenders",
                  "Save time with one application assessed by multiple lenders",
                  "Get expert guidance through the entire process",
                  "No cost to you - lenders pay our commission",
                  "Access exclusive rates not available to the public",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-accent flex items-center justify-center shrink-0 mt-0.5">
                      <FiCheck className="size-3.5 text-accent-foreground" />
                    </div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-xl">
              <h3 className="font-serif text-2xl mb-6 text-center">
                How it works
              </h3>
              <div className="space-y-6">
                {[
                  {
                    step: "1",
                    title: "Tell us your goals",
                    desc: "Share your situation and what you're looking for",
                  },
                  {
                    step: "2",
                    title: "We find your options",
                    desc: "We compare loans across 70+ lenders",
                  },
                  {
                    step: "3",
                    title: "Choose your loan",
                    desc: "Pick the best option with our guidance",
                  },
                  {
                    step: "4",
                    title: "We handle the rest",
                    desc: "We manage the application to settlement",
                  },
                ].map((item) => (
                  <div key={item.step} className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-serif text-lg shrink-0">
                      {item.step}
                    </div>
                    <div>
                      <h4 className="font-medium">{item.title}</h4>
                      <p className="text-sm text-muted-foreground">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Lenders */}
      <section className="py-16 md:py-24 bg-white text-foreground">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">Featured Lenders</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl mb-4">
              Our trusted partners
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              From the big four banks to specialist lenders, we work with
              Australia's most trusted financial institutions to find your
              perfect loan.
            </p>
          </div>

          {/* Lender Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {lenders.map((lender) => (
              <LenderCard key={lender.id} lender={lender} />
            ))}
          </div>

          {/* CTA Note */}
          <div className="mt-12 text-center bg-muted rounded-2xl p-8">
            <p className="font-serif text-2xl mb-2">Find Your Perfect Lender</p>
            <p className="text-muted-foreground mb-6">
              Including specialist lenders for self-employed, SMSF, non-resident,
              and unique situations.
            </p>
            <Button asChild className="btn-pill">
              <Link to="/contact">
                Ask About Your Options
                <FiArrowRight className="ml-2 size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 py-4 pb-8">
        <div className="bg-primary rounded-[2.5rem] py-16 md:py-24 px-6 md:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <p className="section-label mb-4">Ready to Compare?</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-primary-foreground mb-4">
              Find your perfect lender today
            </h2>
            <p className="text-lg text-primary-foreground/90 mb-8 max-w-xl mx-auto">
              Book a free consultation and we'll compare your options across our
              entire lender panel.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                asChild
                size="lg"
                className="btn-pill bg-accent text-accent-foreground hover:bg-accent/90 px-8 h-12"
              >
                <Link to="/contact">
                  Book Free Consultation
                  <FiArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                className="btn-pill bg-white/10 border border-white/20 text-white hover:bg-white/20 px-8 h-12"
              >
                <a href={`tel:${broker.phone.replace(/\s/g, "")}`}>
                  <FiPhone className="mr-2 size-4" />
                  {broker.phone}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
